import { useEffect, useState } from 'react';
import { UpdateItemProcesso } from '@services/ItensProcesso/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import ButtonComp from '@components/UI/Button/Index.jsx';
import ValidationsItemProcesso from '@validations/ItemProcesso/Validations.jsx';
import InputTextModalComp from '@components/UI/InputTextModal/Index.jsx';
import SelectOptionModalComp from '@components/UI/SelectOptionModal/Index.jsx';
import { etapaProcessoOptions } from "@enums/EtapasProcesso/Index.jsx";
import { flgStatusOptions } from '@enums/FlgStatus/Index.jsx';
import { useFetchDataCascade } from '@hooks/api/useFetchDataCascade.js';
import { Modal } from 'antd';

function EditModal({ onUpdate, visible, closeModal, selectedRow }) {
  const [codigo, setCodigo] = useState('');

  const {
    yearOptions,
    serviceOptions,
    typeOptions,
    processOptions,
    yearLoading,
    serviceLoading,
    typeLoading,
    processLoading,
    fetchYears,
    fetchServicesByYear,
    fetchTypesByServiceId,
    fetchProcessosByType,
    setServiceOptions,
    setTypeOptions,
    setProcessOptions,
  } = useFetchDataCascade();

  const [tempValues, setTempValues] = useState({
    ano: null,
    servico: null,
    tipo: null,
    nomeProcesso: null,
    etapaProcesso: null,
    nomeItem: '',
    descricao: '',
    flgStatus: null,
  });

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedRow) {
      setCodigo(selectedRow.codigo);
      setTempValues({
        ano: { value: selectedRow.ano, label: selectedRow.ano },
        servico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
        tipo: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
        nomeProcesso: { value: selectedRow.idProcesso, label: selectedRow.nomeProcesso },
        etapaProcesso: etapaProcessoOptions.find(option => option.value === selectedRow.etapaProcesso),
        nomeItem: selectedRow.nomeItem,
        descricao: selectedRow.descricao,
        flgStatus: selectedRow.flgStatus,
      });

      if (selectedRow.ano) {
        fetchServicesByYear(selectedRow.ano);
      }
      if (selectedRow.idTipoServico) {
        fetchTypesByServiceId(selectedRow.idTipoServico);
      }
      if (selectedRow.idTipoServicoAssociado) {
        fetchProcessosByType(selectedRow.idTipoServicoAssociado);
      }
    }
  }, [selectedRow]);

  const checkIsDirty = () => {
    return (
      selectedRow.ano !== tempValues.ano?.value ||
      selectedRow.idTipoServico !== tempValues.servico?.value ||
      selectedRow.idTipoServicoAssociado !== tempValues.tipo?.value ||
      selectedRow.idProcesso !== tempValues.nomeProcesso?.value ||
      selectedRow.etapaProcesso !== tempValues.etapaProcesso?.value ||
      selectedRow.nomeItem !== tempValues.nomeItem ||
      selectedRow.descricao !== tempValues.descricao ||
      selectedRow.flgStatus !== tempValues.flgStatus
    );
  };

  const handleTempChange = (field, value) => {
    setTempValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "ano") {
      setServiceOptions([]);
      setTypeOptions([]);
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        servico: null,
        tipo: null,
        nomeProcesso: null,
      }));
      if (value) {
        fetchServicesByYear(value.value);
      }
    }

    if (field === "servico") {
      setTypeOptions([]);
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        tipo: null,
        nomeProcesso: null,
      }));
      if (value) {
        fetchTypesByServiceId(value.value);
      }
    }

    if (field === "tipo") {
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        nomeProcesso: null,
      }));
      if (value) {
        fetchProcessosByType(value.value);
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (!checkIsDirty()) {
        SweetAlert.error("Erro", "Nenhuma alteração foi feita nos dados.");
        return;
      }

      const { validateFields } = ValidationsItemProcesso();
      const errors = validateFields(tempValues);

      if (Object.keys(errors).length > 0) {
        const errorMessage = Object.values(errors).join("\n");
        SweetAlert.error("Erro na Validação", errorMessage);
        return;
      }

      const payload = {
        idProcesso: tempValues.nomeProcesso?.value,
        etapaProcesso: tempValues.etapaProcesso?.value,
        descricao: tempValues.descricao,
        nomeItem: tempValues.nomeItem,
        flgStatus: tempValues.flgStatus,
      };

      SweetAlert.loading();

      const result = await UpdateItemProcesso(selectedRow.id, payload);

      onUpdate({ ...result });

      closeModal();

      SweetAlert.success('Atualização Concluída!', 'Os dados foram atualizados com sucesso.');
    } catch (error) {

      const errorMessage = error.parsed?.message || 'Não foi possível atualizar o item do processo.';

      SweetAlert.errorHTML('Erro ao atualizar', errorMessage);
      console.error("Erro ao salvar atualizações", errorMessage);
    }
  };

  const handleCloseWithReset = () => {
    setTempValues({
      ano: { value: selectedRow.ano, label: selectedRow.ano },
      servico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
      tipo: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
      nomeProcesso: { value: selectedRow.idProcesso, label: selectedRow.nomeProcesso },
      etapaProcesso: etapaProcessoOptions.find(option => option.value === selectedRow.etapaProcesso),
      nomeItem: selectedRow.nomeItem,
      descricao: selectedRow.descricao,
      flgStatus: selectedRow.flgStatus,
    });
    closeModal();
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      centered
      footer={null}
      width={800}
      title={<h5 className="fw-bold">Edição de Item do Processo</h5>}
    >
      <hr />

      <InputTextModalComp
        label="Código"
        value={codigo}
        placeholder="Código"
        disabled
      />

      <SelectOptionModalComp
        label="Ano"
        options={yearOptions}
        value={tempValues.ano}
        onChange={(selected) => handleTempChange('ano', selected)}
        placeholder="Selecionar um ano"
        hoverMessage="Selecionar um ano"
        isLoading={yearLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Serviço"
        options={serviceOptions}
        value={tempValues.servico}
        onChange={(selected) => handleTempChange('servico', selected)}
        placeholder="Selecionar um serviço"
        hoverMessage="Selecionar um serviço"
        isLoading={serviceLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Tipo"
        options={typeOptions}
        value={tempValues.tipo}
        onChange={(selected) => handleTempChange('tipo', selected)}
        placeholder="Selecionar um tipo"
        hoverMessage="Selecionar um tipo"
        isLoading={typeLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Processo"
        options={processOptions}
        value={tempValues.nomeProcesso}
        onChange={(selected) => handleTempChange('nomeProcesso', selected)}
        placeholder="Selecionar um processo"
        hoverMessage="Selecionar um processo"
        isLoading={processLoading}
        isDisabled
      />

      {/* Campo desabilitado para edição, possível gap relacionado as solicitações existentes se habilitado */}
      <SelectOptionModalComp
        label="Etapa do Processo"
        options={etapaProcessoOptions}
        value={tempValues.etapaProcesso}
        onChange={(selected) => handleTempChange('etapaProcesso', selected)}
        placeholder="-"
        hoverMessage="Campo desabilitado para edição"
        isDisabled
      />

      <InputTextModalComp
        label="Item do Processo"
        value={tempValues.nomeItem}
        onChange={(e) => handleTempChange('nomeItem', e.target.value)}
        placeholder="Preencher com um item do processo"
        hoverMessage="Preencher com um item do processo"
        maxLength={255}
      />

      <InputTextModalComp
        label="Descrição"
        value={tempValues.descricao}
        onChange={(e) => handleTempChange('descricao', e.target.value)}
        placeholder="Preencher com uma descrição"
        hoverMessage="Preencher com uma descrição"
        maxLength={255}
      />

      <SelectOptionModalComp
        label="Status"
        options={flgStatusOptions}
        value={flgStatusOptions.find(option => option.value === tempValues.flgStatus)}
        onChange={(option) => handleTempChange("flgStatus", option.value)}
        placeholder="Selecionar o status"
        hoverMessage="Selecionar o status"
      />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          title="ATUALIZAR"
          buttonColor="#2b97be"
          classnameIcon="fa-regular fa-floppy-disk"
          onClick={onSubmit}
          hoverMessage={"Botão para confirmar a atualização"}
        />

        <ButtonComp
          title="CANCELAR"
          buttonColor="#dc3545"
          classnameIcon="fa-regular fa-circle-xmark"
          onClick={handleCloseWithReset}
          hoverMessage={"Botão para cancelar a atualização"}
        />
      </div>

    </Modal >
  );
}

export default EditModal;